// Illustrative graphics and product mocks rendered inside the signup brand
// panel carousels. Purely presentational.
import * as React from "react";

import { agentAppIconSrc } from "@replohq/lattice/icons/agentAppIconSrc";
import { cn } from "@replohq/lattice/utils/cn";
import {
  ArrowUp,
  BarChart3,
  ChevronDown,
  ChevronsLeftRight,
  ChevronsUpDown,
  Database,
  FileSpreadsheet,
  FileText,
  Globe,
  ImageIcon,
  LayoutTemplate,
  Minimize2,
  MousePointer2,
  MoveHorizontal,
  MoveVertical,
  Type,
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";

import cometeerOriginal from "../../../../../../replo-agent/src/agent-ui/assets/activation/cometeer-original.webp";
import grazaOriginal from "../../../../../../replo-agent/src/agent-ui/assets/activation/graza-original.webp";
import storeMerch from "./assets/context-store-merch-unbranded.jpg";
import storeStreetwearCloseup from "./assets/context-store-streetwear-closeup-unbranded.jpg";
import storeStreetwear from "./assets/context-store-streetwear-unbranded.jpg";

const miniappSlideItems = [
  { name: "Site Builder", iconSrc: agentAppIconSrc.siteBuilder },
  { name: "Plans", iconSrc: agentAppIconSrc.plans },
  { name: "Assets", iconSrc: agentAppIconSrc.assets },
  { name: "Integrations", iconSrc: agentAppIconSrc.integrations },
  { name: "Insights", iconSrc: agentAppIconSrc.insights },
  { name: "Tasks", iconSrc: agentAppIconSrc.tasks },
  { name: "Branding", iconSrc: agentAppIconSrc.brandStudio },
  { name: "Skills", iconSrc: agentAppIconSrc.skills },
  { name: "Reploverse", iconSrc: agentAppIconSrc.reploverse },
  { name: "Settings", iconSrc: agentAppIconSrc.settings },
] as const;

const integrationSlideItems = [
  { name: "Shopify", logoSrc: "/images/integration-logos/shopify.png" },
  { name: "Klaviyo", logoSrc: "/images/integration/logos/klaviyo.svg" },
  { name: "Google Ads", logoSrc: "/images/integration/logos/google-ads.svg" },
  {
    name: "Google Analytics",
    logoSrc: "/images/integration/logos/google-analytics.svg",
  },
  { name: "Attentive", logoSrc: "/images/integration/logos/attentive.png" },
  { name: "Postscript", logoSrc: "/images/integration/logos/postscript.png" },
  { name: "Okendo", logoSrc: "/images/integration/logos/okendo.svg" },
  { name: "Yotpo", logoSrc: "/images/integration/logos/yotpo.png" },
  { name: "Recharge", logoSrc: "/images/integration/logos/recharge.png" },
  { name: "Rebuy", logoSrc: "/images/integration/logos/rebuy.png" },
  { name: "PostHog", logoSrc: "/images/integration/logos/posthog.svg" },
  { name: "Segment", logoSrc: "/images/integration/logos/segment.svg" },
] as const;

function PaperCard({
  children,
  className,
  style,
}: React.PropsWithChildren<{
  className?: string;
  style?: React.CSSProperties;
}>) {
  return (
    <div
      className={cn(
        "rounded-xl border-0.5 border-border bg-sheet text-default shadow-sm",
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

function DraggingCursorIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path
        d="M8.25 11.2V6.05a1.35 1.35 0 0 1 2.7 0v4.15m0-.35V4.7a1.35 1.35 0 0 1 2.7 0v5.15m0-.55V6.05a1.35 1.35 0 0 1 2.7 0v5m0-1.85V8.05a1.35 1.35 0 0 1 2.7 0v4.95c0 4.55-2.35 7-6.15 7h-1.65c-2.2 0-3.65-.85-4.8-2.7l-1.6-2.55a1.45 1.45 0 0 1 2.45-1.55l1.3 1.75"
        fill="var(--color-sheet)"
        stroke="var(--color-default)"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export function ThemeEditorGraphic() {
  return (
    <PaperCard
      className="relative h-[270px] w-full max-w-[510px] overflow-hidden bg-surface p-0"
      style={{
        backgroundImage:
          "radial-gradient(circle, color-mix(in srgb, var(--color-muted) 22%, transparent) 1px, transparent 1px)",
        backgroundSize: "18px 18px",
      }}
    >
      <div className="absolute left-8 top-5 flex h-[230px] w-[210px] items-center justify-center">
        <div className="relative flex h-[230px] w-[190px] flex-col gap-2 rounded-lg border-0.5 border-border bg-sheet p-3 shadow-sm">
          <div className="rounded-md bg-surface p-2">
            <span className="block h-2 w-16 rounded-full bg-muted/20" />
            <div className="mt-2 flex gap-2">
              <span className="h-12 flex-1 rounded bg-neutral-soft" />
              <span className="flex flex-1 flex-col justify-center gap-1">
                <span className="h-1.5 w-full rounded-full bg-muted/20" />
                <span className="h-1.5 w-4/5 rounded-full bg-muted/20" />
                <span className="mt-1 h-3 w-10 rounded bg-neutral-soft" />
              </span>
            </div>
          </div>
          <span className="h-12 rounded-md border border-dashed border-sky/55 bg-sky-soft/35" />
          <div className="grid flex-1 grid-cols-2 gap-2">
            <span className="rounded-md bg-surface" />
            <span className="flex flex-col gap-1 rounded-md bg-sheet p-2">
              <span className="h-1.5 w-full rounded-full bg-muted/15" />
              <span className="h-1.5 w-5/6 rounded-full bg-muted/15" />
              <span className="mt-auto h-3 w-10 rounded bg-neutral-soft" />
            </span>
          </div>
        </div>
        <PaperCard className="absolute left-8 top-[100px] flex w-32 rotate-2 cursor-grabbing items-center gap-2 rounded-lg bg-sheet p-2 shadow-md">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface">
            <LayoutTemplate className="size-3 text-muted" />
          </span>
          <span className="flex flex-col gap-1">
            <span className="text-[8px] font-medium">Product block</span>
            <span className="h-1 w-10 rounded-full bg-muted/20" />
          </span>
        </PaperCard>
        <DraggingCursorIcon className="absolute left-[128px] top-[126px] size-7 -rotate-6 drop-shadow-[0_2px_4px_rgba(0,0,0,0.24)]" />
      </div>
      <div className="absolute right-8 top-5 flex h-[230px] w-[224px] flex-col overflow-hidden rounded-xl border-0.5 border-border bg-sheet shadow-sm">
        <div className="flex flex-1 flex-col overflow-hidden text-default">
          <RightBarModifierMock title="Size">
            <div className="grid grid-cols-[42px_1fr] items-center gap-x-2 gap-y-2 text-[10px]">
              <span className="text-muted">Width</span>
              <span className="grid grid-cols-[84px_64px] gap-2">
                <span className="grid grid-cols-3 overflow-hidden rounded-md bg-surface">
                  <span className="flex size-7 items-center justify-center rounded-md border-0.5 border-border bg-sheet text-sky">
                    <ChevronsLeftRight className="size-3" />
                  </span>
                  <span className="flex size-7 items-center justify-center text-muted">
                    <Minimize2 className="size-3 rotate-45" />
                  </span>
                  <span className="flex size-7 items-center justify-center text-muted">
                    <MoveHorizontal className="size-3" />
                  </span>
                </span>
                <span className="flex items-center justify-between rounded-md border-0.5 border-border bg-sheet px-2 py-1">
                  Fill
                  <ChevronDown className="size-2.5 text-muted" />
                </span>
              </span>
              <span className="text-muted">Height</span>
              <span className="grid grid-cols-[84px_64px] gap-2">
                <span className="grid grid-cols-3 overflow-hidden rounded-md bg-surface">
                  <span className="flex size-7 items-center justify-center text-muted">
                    <ChevronsUpDown className="size-3" />
                  </span>
                  <span className="flex size-7 items-center justify-center rounded-md border-0.5 border-border bg-sheet text-sky">
                    <MoveVertical className="size-3" />
                  </span>
                  <span className="flex size-7 items-center justify-center text-muted">
                    <Minimize2 className="size-3 -rotate-45" />
                  </span>
                </span>
                <span className="flex items-center justify-between rounded-md border-0.5 border-border bg-sheet px-2 py-1">
                  Wrap
                  <ChevronDown className="size-2.5 text-muted" />
                </span>
              </span>
            </div>
          </RightBarModifierMock>
          <RightBarModifierMock title="Layout">
            <div className="grid grid-cols-2 overflow-hidden rounded-md bg-surface text-center text-[10px]">
              <span className="rounded-md border-0.5 border-border bg-sheet py-1 text-sky">
                Auto layout
              </span>
              <span className="py-1 text-muted">Columns</span>
            </div>
            <div className="grid h-16 grid-cols-[1fr_110px] gap-2">
              <div className="flex h-7 overflow-hidden rounded-md bg-surface text-[12px]">
                <span className="flex w-9 items-center justify-center rounded-md border-0.5 border-border bg-sheet text-sky">
                  &rarr;
                </span>
                <span className="flex w-9 items-center justify-center text-muted">
                  &darr;
                </span>
                <span className="flex w-9 items-center justify-center text-muted">
                  R
                </span>
              </div>
              <div className="relative rounded bg-surface">
                {[0, 1, 2, 3, 4, 5].map((dotIndex) => (
                  <span
                    key={dotIndex}
                    className={cn(
                      "absolute size-1.5 rounded-full bg-muted/35",
                      dotIndex === 0 && "left-2 top-2",
                      dotIndex === 1 && "left-1/2 top-2 -translate-x-1/2",
                      dotIndex === 2 && "right-2 top-2",
                      dotIndex === 3 && "bottom-2 left-2",
                      dotIndex === 4 && "bottom-2 left-1/2 -translate-x-1/2",
                      dotIndex === 5 && "bottom-2 right-2",
                    )}
                  />
                ))}
                <span className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1">
                  <span className="h-10 w-1.5 rounded-full bg-sky" />
                  <span className="h-12 w-1.5 rounded-full bg-sky" />
                  <span className="h-9 w-1.5 rounded-full bg-sky" />
                </span>
              </div>
            </div>
          </RightBarModifierMock>
        </div>
      </div>
    </PaperCard>
  );
}

function RightBarModifierMock({
  title,
  children,
}: React.PropsWithChildren<{ title: string }>) {
  return (
    <div className="border-b-0.5 border-border p-2 last:border-b-0">
      <div className="mb-1.5 flex items-center">
        <span className="text-[11px] font-semibold">{title}</span>
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

export function TemplateGalleryGraphic() {
  const templates = [
    {
      name: "Hero",
      variant: "hero",
      className: "border-sky-border bg-sky-soft",
    },
    {
      name: "Product",
      variant: "product",
      className: "border-border bg-sheet",
    },
    {
      name: "Collection",
      variant: "collection",
      className: "border-border bg-sheet",
    },
    {
      name: "Promo",
      variant: "promo",
      className: "border-border bg-sheet",
    },
    {
      name: "Reviews",
      variant: "product",
      className: "border-border bg-sheet",
    },
    {
      name: "Feature row",
      variant: "collection",
      className: "border-border bg-sheet",
    },
  ] as const;

  return (
    <PaperCard className="relative flex h-[260px] w-full max-w-[430px] flex-col gap-3 overflow-hidden p-4">
      <div className="flex items-center gap-2">
        <LayoutTemplate className="size-4 text-muted" />
        <span className="text-sm font-semibold">Template marketplace</span>
      </div>
      <div className="grid grid-cols-2 gap-3 overflow-hidden">
        {templates.map((template) => (
          <div
            key={template.name}
            className={cn(
              "flex h-[88px] flex-col gap-1.5 rounded-lg border-0.5 p-2 shadow-sm",
              template.className,
            )}
          >
            <TemplateCardPlaceholder variant={template.variant} />
            <span className="text-[10px] font-medium">{template.name}</span>
          </div>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-sheet to-sheet/0" />
    </PaperCard>
  );
}

function TemplateCardPlaceholder({
  variant,
}: {
  variant: "hero" | "product" | "collection" | "promo";
}) {
  return exhaustiveSwitch({ type: variant })({
    hero: () => (
      <div className="grid flex-1 grid-cols-[1.1fr_0.9fr] gap-2 rounded-md bg-sheet p-2">
        <span className="flex flex-col justify-center gap-1">
          <span className="h-1.5 w-12 rounded-full bg-sky" />
          <span className="h-1 w-full rounded-full bg-sky/20" />
          <span className="h-1 w-2/3 rounded-full bg-sky/20" />
        </span>
        <span className="rounded bg-sky-soft" />
      </div>
    ),
    product: () => (
      <div className="grid flex-1 grid-cols-[0.8fr_1fr] gap-2 rounded-md bg-surface p-2">
        <span className="rounded bg-neutral-soft" />
        <span className="flex flex-col gap-1">
          <span className="h-1.5 w-10 rounded-full bg-default" />
          <span className="h-1 w-full rounded-full bg-muted/20" />
          <span className="h-1 w-2/3 rounded-full bg-muted/20" />
          <span className="mt-auto h-2.5 w-12 rounded bg-sheet" />
        </span>
      </div>
    ),
    collection: () => (
      <div className="grid flex-1 grid-cols-3 gap-1.5 rounded-md bg-surface p-2">
        {[0, 1, 2].map((placeholderIndex) => (
          <span
            key={placeholderIndex}
            className="flex flex-col gap-1 rounded bg-sheet p-1"
          >
            <span className="h-4 rounded bg-neutral-soft" />
            <span className="h-1 rounded-full bg-muted/20" />
          </span>
        ))}
      </div>
    ),
    promo: () => (
      <div className="grid flex-1 grid-cols-[1fr_38px] gap-2 rounded-md bg-surface p-2">
        <span className="flex flex-col justify-center gap-1">
          <span className="h-1.5 w-12 rounded-full bg-default" />
          <span className="h-1 w-full rounded-full bg-muted/20" />
          <span className="h-1 w-2/3 rounded-full bg-muted/20" />
        </span>
        <span className="rounded bg-neutral-soft" />
      </div>
    ),
  });
}

export function ShopifyContentGraphic() {
  const shouldReduceMotion = useReducedMotion();
  const dynamicDataFields = [
    {
      label: "Product",
      value: "Hydration set",
      connectorY: 73,
      connectorPath:
        "M 138 73 H 226 Q 234 73 234 65 V 55 Q 234 47 242 47 H 302",
    },
    {
      label: "Price",
      value: "$48.00",
      connectorY: 123,
      connectorPath:
        "M 138 123 H 232 Q 240 123 240 115 V 106 Q 240 98 248 98 H 302",
    },
    {
      label: "Reviews",
      value: "4.8 stars",
      connectorY: 173,
      connectorPath:
        "M 138 173 H 216 Q 224 173 224 165 V 31 Q 224 23 232 23 H 302",
    },
  ];

  return (
    <div className="relative grid w-full max-w-[460px] grid-cols-[150px_1fr] gap-3">
      <PaperCard className="flex h-[250px] flex-col gap-3 p-3">
        <div className="flex items-center gap-2">
          <span className="flex size-7 items-center justify-center rounded-lg bg-purple text-white shadow-sm">
            <Database className="size-3.5" />
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-semibold">Dynamic data</span>
            <span className="text-[9px] text-purple">Shopify fields</span>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {dynamicDataFields.map((contentField) => (
            <div
              key={contentField.label}
              className="flex h-[38px] flex-col justify-center rounded-lg border-0.5 border-purple-border bg-purple-soft p-2"
            >
              <span className="flex items-center gap-1 text-[8px] text-purple">
                <Database className="size-2.5" />
                {contentField.label}
              </span>
              <span className="text-[10px] font-medium">
                {contentField.value}
              </span>
            </div>
          ))}
        </div>
      </PaperCard>
      <svg
        viewBox="0 0 460 250"
        className="pointer-events-none absolute inset-0 z-10 size-full"
        aria-hidden
      >
        {dynamicDataFields.map((contentField, index) => (
          <g key={contentField.label}>
            <circle
              cx="138"
              cy={contentField.connectorY}
              r="3"
              fill="var(--color-purple)"
            />
            <path
              d={contentField.connectorPath}
              fill="none"
              stroke="color-mix(in srgb, var(--color-purple) 36%, transparent)"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {!shouldReduceMotion && (
              <motion.path
                d={contentField.connectorPath}
                fill="none"
                stroke="color-mix(in srgb, var(--color-purple) 58%, transparent)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, pathOffset: 0, opacity: 0 }}
                animate={{
                  pathLength: [0, 0.22, 0],
                  pathOffset: [0, 0.78, 1],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 2.4,
                  ease: "easeInOut",
                  repeat: Infinity,
                  delay: index * 0.18,
                }}
              />
            )}
          </g>
        ))}
      </svg>
      <PaperCard className="relative flex h-[250px] flex-col overflow-hidden p-0">
        <div className="relative grid flex-1 grid-cols-[112px_1fr] gap-3 bg-sheet p-3">
          <div className="flex flex-col gap-2">
            <div className="h-[164px] rounded-lg bg-surface p-2">
              <div className="h-full rounded-md bg-neutral-soft" />
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {[0, 1, 2].map((thumbnailIndex) => (
                <span
                  key={thumbnailIndex}
                  className={cn(
                    "h-8 rounded-md border-0.5 bg-surface",
                    thumbnailIndex === 0 ? "border-sky" : "border-border",
                  )}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-lg border-0.5 border-border bg-sheet p-3">
            <div className="flex items-center gap-1.5 text-[9px] text-muted">
              <span className="text-sky">★★★★★</span>
              <span>4.8 reviews</span>
            </div>
            <span className="h-2.5 w-28 rounded-full bg-default" />
            <span className="h-1.5 w-full rounded-full bg-muted/20" />
            <span className="h-1.5 w-4/5 rounded-full bg-muted/20" />
            <span className="text-xs font-semibold">$48.00</span>
            <div className="grid grid-cols-[48px_1fr] gap-2">
              <span className="rounded-md border-0.5 border-border bg-sheet px-2 py-1 text-center text-[9px]">
                1
              </span>
              <span className="rounded-md bg-default px-2 py-1 text-center text-[9px] font-medium text-sheet">
                Buy now
              </span>
            </div>
            <div className="mt-auto flex flex-col gap-1">
              <span className="h-1.5 w-full rounded-full bg-muted/15" />
              <span className="h-1.5 w-2/3 rounded-full bg-muted/15" />
            </div>
          </div>
        </div>
      </PaperCard>
    </div>
  );
}

export function ShopifyThemePublishGraphic() {
  const shouldReduceMotion = useReducedMotion();
  const publishConnectorPath = "M 250 130 L 282 130";

  return (
    <div
      className="relative h-[260px] w-full max-w-[460px] overflow-hidden rounded-xl bg-surface"
      style={{
        backgroundImage:
          "radial-gradient(circle, color-mix(in srgb, var(--color-muted) 22%, transparent) 1px, transparent 1px)",
        backgroundSize: "18px 18px",
      }}
    >
      <svg
        viewBox="0 0 460 260"
        className="pointer-events-none absolute inset-0 z-[1] size-full"
        aria-hidden
      >
        <path
          d={publishConnectorPath}
          fill="none"
          stroke="color-mix(in srgb, var(--color-muted) 22%, transparent)"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {!shouldReduceMotion && (
          <motion.path
            d={publishConnectorPath}
            fill="none"
            stroke="color-mix(in srgb, #008060 64%, transparent)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, pathOffset: 0, opacity: 0 }}
            animate={{
              pathLength: [0, 0.2, 0],
              pathOffset: [0, 0.78, 1],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2.4,
              ease: "easeInOut",
              repeat: Infinity,
            }}
          />
        )}
      </svg>

      <PaperCard className="absolute left-10 top-5 z-10 flex h-[220px] w-[210px] flex-col overflow-hidden p-0">
        <div className="flex h-9 items-center justify-between border-b-0.5 border-border px-3">
          <span className="max-w-[132px] truncate rounded-md bg-surface px-2 py-1 text-[8px] font-medium text-muted">
            /pages/summer-hydration-kit
          </span>
          <span className="rounded-md bg-default px-2.5 py-1 text-[8px] font-medium text-sheet shadow-sm">
            Publish
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-2 bg-sheet p-3">
          <div className="grid grid-cols-[0.9fr_1.1fr] gap-2 rounded-lg bg-surface p-2">
            <span className="h-16 rounded-md bg-neutral-soft" />
            <span className="flex flex-col justify-center gap-1">
              <span className="h-2 w-16 rounded-full bg-default" />
              <span className="h-1.5 w-full rounded-full bg-muted/20" />
              <span className="h-1.5 w-4/5 rounded-full bg-muted/20" />
              <span className="mt-1 h-3 w-12 rounded bg-sheet" />
            </span>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-2">
            <span className="rounded-lg bg-surface" />
            <span className="flex flex-col gap-1 rounded-lg bg-sheet p-2">
              <span className="h-1.5 w-full rounded-full bg-muted/15" />
              <span className="h-1.5 w-4/5 rounded-full bg-muted/15" />
              <span className="mt-auto h-3 w-12 rounded bg-neutral-soft" />
            </span>
          </div>
        </div>
      </PaperCard>

      <PaperCard className="absolute right-9 top-[104px] z-20 flex w-[142px] items-center gap-2 p-3">
        <div className="flex items-center gap-2">
          <span className="flex size-7 items-center justify-center rounded-lg bg-sheet p-1.5 shadow-sm">
            <img
              src="/images/shopify-green-logo.svg"
              alt=""
              className="size-full object-contain"
            />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-xs font-semibold">Shopify</span>
            <span className="text-[9px] text-muted">Online store</span>
          </div>
        </div>
      </PaperCard>
    </div>
  );
}

export function MiniappsGraphic() {
  return (
    <div className="relative flex h-[300px] w-full max-w-[430px] flex-col items-center justify-center">
      <div className="grid w-full grid-cols-5 gap-x-6 gap-y-8">
        {miniappSlideItems.map((item) => (
          <span
            key={item.name}
            className="relative z-10 flex items-center justify-center"
          >
            <img
              src={item.iconSrc}
              alt={item.name}
              className="size-16 rounded-2xl object-contain"
            />
          </span>
        ))}
      </div>
    </div>
  );
}

const integrationGraphicCenter = { x: 230, y: 120 } as const;

const integrationLogosWithoutPadding = new Set([
  "Attentive",
  "Okendo",
  "Yotpo",
  "Recharge",
]);

const integrationLogosWithRoundedClip = new Set([
  "Attentive",
  "Okendo",
  "Yotpo",
  "Recharge",
]);

const integrationLogoBackgroundClassNames: Record<string, string> = {
  Attentive: "bg-[#fff382]",
  Okendo: "bg-[#00eab6]",
  Yotpo: "bg-[#0042e4]",
  Recharge: "bg-[#3901f1]",
};

function getRoundedIntegrationConnectorPath({
  endX,
  endY,
}: {
  endX: number;
  endY: number;
}) {
  const startX = integrationGraphicCenter.x;
  const startY = integrationGraphicCenter.y;
  const horizontalDirection = endX >= startX ? 1 : -1;
  const verticalDirection = endY >= startY ? 1 : -1;
  const bendX = startX + (endX - startX) * 0.58;
  const cornerRadius = Math.min(
    18,
    Math.abs(endX - startX) / 3,
    Math.abs(endY - startY) / 2,
  );

  return [
    `M ${startX} ${startY}`,
    `L ${bendX - cornerRadius * horizontalDirection} ${startY}`,
    `Q ${bendX} ${startY} ${bendX} ${
      startY + cornerRadius * verticalDirection
    }`,
    `L ${bendX} ${endY - cornerRadius * verticalDirection}`,
    `Q ${bendX} ${endY} ${bendX + cornerRadius * horizontalDirection} ${endY}`,
    `L ${endX} ${endY}`,
  ].join(" ");
}

export function IntegrationsGraphic() {
  const shouldReduceMotion = useReducedMotion();
  const logoPositions = [
    { item: integrationSlideItems[0], x: 48, y: 26 },
    { item: integrationSlideItems[1], x: 124, y: 72 },
    { item: integrationSlideItems[2], x: 412, y: 26 },
    { item: integrationSlideItems[3], x: 44, y: 120 },
    { item: integrationSlideItems[4], x: 124, y: 168 },
    { item: integrationSlideItems[5], x: 48, y: 214 },
    { item: integrationSlideItems[6], x: 336, y: 72 },
    { item: integrationSlideItems[7], x: 416, y: 120 },
    { item: integrationSlideItems[8], x: 336, y: 168 },
    { item: integrationSlideItems[9], x: 412, y: 214 },
    { item: integrationSlideItems[10], x: 230, y: 24 },
    { item: integrationSlideItems[11], x: 230, y: 216 },
  ];

  return (
    <div className="relative h-[240px] w-full max-w-[460px]">
      <svg
        viewBox="0 0 460 240"
        className="absolute inset-0 size-full"
        aria-hidden
      >
        {logoPositions.map(({ item, x, y }, index) => {
          const connectorPath = getRoundedIntegrationConnectorPath({
            endX: x,
            endY: y,
          });

          return (
            <g key={item.name}>
              <path
                d={connectorPath}
                fill="none"
                stroke="rgba(255,254,250,0.22)"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {!shouldReduceMotion && (
                <motion.path
                  d={connectorPath}
                  fill="none"
                  stroke="rgba(255,254,250,0.42)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0, pathOffset: 0, opacity: 0 }}
                  animate={{
                    pathLength: [0, 0.2, 0],
                    pathOffset: [0, 0.78, 1],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2.4,
                    ease: "easeInOut",
                    repeat: Infinity,
                    delay: index * 0.14,
                  }}
                />
              )}
            </g>
          );
        })}
      </svg>

      <PaperCard className="absolute left-1/2 top-1/2 z-20 flex size-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-[1.65rem] bg-brand-seashell p-0">
        <svg
          viewBox="0 0 40 40"
          className="size-full text-brand-seashell"
          aria-hidden
        >
          <rect width="40" height="40" rx="8" fill="black" />
          <rect
            x="0.208333"
            y="0.208333"
            width="39.5833"
            height="39.5833"
            rx="7.79167"
            stroke="currentColor"
            strokeOpacity="0.5"
            strokeWidth="0.416667"
          />
          <path
            d="M10.922 30H15.7728C16.3686 30 16.8508 29.5178 16.8508 28.922V25.1771C16.8508 24.922 17.049 24.7228 17.305 24.7228C17.5033 24.7228 17.7025 24.8644 17.7593 25.0635C18.9508 28.8085 23.0644 29.9712 26.1279 29.9712H29.0779C29.6736 29.9712 30.1558 29.489 30.1558 28.8932V24.0135C30.1558 23.4178 29.6736 22.9356 29.0779 22.9356H24.8228C24.3406 22.9356 24.1135 22.3686 24.4542 22.0279L29.1635 17.3186C29.5889 16.8932 29.8161 16.3254 29.8161 15.7296V11.078C29.8161 10.4822 29.3339 10 28.7381 10H24.0576C23.4907 10 22.9229 10.2271 22.4974 10.6525L17.7881 15.3619C17.4474 15.7026 16.8517 15.4466 16.8517 14.9644V11.078C16.8517 10.4822 16.3695 10 15.7737 10H11.177C10.5813 10 10.0991 10.4822 10.0991 11.078V15.6449C10.0991 16.2406 10.5813 16.7228 11.177 16.7228H15.0914C15.5736 16.7228 15.8007 17.3186 15.46 17.6593L12.9634 19.9856C11.1491 21.6881 9.84399 24.0703 9.84399 26.5669V28.9211C9.84399 29.5169 10.3262 29.9991 10.922 29.9991V30ZM20.7092 23.1347C19.3762 23.1068 18.1847 23.2195 17.3339 22.6237C16.511 21.9144 16.6534 20.6949 16.5966 19.6737C16.6245 18.6525 16.5398 17.3195 17.2203 16.6381C17.9008 15.9567 19.2347 15.9856 20.227 16.0144C21.2771 16.0712 22.4965 15.9297 23.2059 16.7517C23.7728 17.6025 23.6601 18.7941 23.6881 20.1271C23.6601 20.95 23.6881 21.8855 23.1211 22.5101L23.0644 22.5669C22.4686 23.1059 21.5042 23.078 20.7102 23.1338L20.7092 23.1347Z"
            fill="currentColor"
          />
        </svg>
      </PaperCard>

      {logoPositions.map(({ item, x, y }) => {
        const isLogoWithoutPadding = integrationLogosWithoutPadding.has(
          item.name,
        );
        const shouldClipLogoToCard = integrationLogosWithRoundedClip.has(
          item.name,
        );
        const logoBackgroundClassName =
          integrationLogoBackgroundClassNames[item.name] ?? "bg-[#FFFEFA]";
        return (
          <span
            key={item.name}
            className="absolute z-10 flex size-12 items-center justify-center"
            style={{
              left: x,
              top: y,
              transform: "translate(-50%, -50%)",
            }}
          >
            <PaperCard
              className={cn(
                "flex size-full items-center justify-center rounded-2xl",
                logoBackgroundClassName,
                shouldClipLogoToCard && "overflow-hidden",
                isLogoWithoutPadding ? "p-0" : "p-2",
              )}
            >
              <img
                src={item.logoSrc}
                alt={item.name}
                className="size-full object-contain"
              />
            </PaperCard>
          </span>
        );
      })}
    </div>
  );
}

export function ContextGraphic() {
  return isOsmanthusEnabled() ? (
    <OsmanthusContextGraphic />
  ) : (
    <LegacyContextGraphic />
  );
}

function OsmanthusContextGraphic() {
  const products = [
    {
      name: "Forest crewneck",
      description: "Heavyweight organic cotton",
      price: "$68",
      reviews: "4.9 (184)",
      imageSrc: storeMerch,
      rotation: "-3deg",
    },
    {
      name: "Studio crew",
      description: "Relaxed everyday fit",
      price: "$72",
      reviews: "4.8 (126)",
      imageSrc: storeStreetwear,
      rotation: "0deg",
    },
    {
      name: "Core collection",
      description: "Limited seasonal color",
      price: "$72",
      reviews: "4.9 (98)",
      imageSrc: storeStreetwearCloseup,
      rotation: "3deg",
    },
  ] as const;

  return (
    <div className="relative flex h-[270px] w-full max-w-[460px] items-center justify-center">
      <div className="flex items-center justify-center gap-3">
        {products.map((product) => (
          <div
            key={product.name}
            className="w-32 overflow-hidden rounded-xl bg-sheet text-left text-default shadow-lg"
            style={{ transform: `rotate(${product.rotation})` }}
          >
            <div className="relative h-36 overflow-hidden bg-neutral-soft">
              <img
                src={product.imageSrc}
                alt={product.name}
                className="size-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-1 p-3">
              <div className="flex min-h-8 items-start justify-between gap-2">
                <span className="text-xs font-semibold">{product.name}</span>
                <span className="text-xs font-semibold">{product.price}</span>
              </div>
              <span className="min-h-6 text-[9px] leading-tight text-muted">
                {product.description}
              </span>
              <span className="text-[9px] text-muted">
                <span className="text-warning">&#9733;</span> {product.reviews}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 right-2 flex items-center gap-2 rounded-xl border-0.5 border-border bg-sheet/90 px-3 py-2 shadow-md supports-backdrop-filter:backdrop-blur-sm">
        <img
          src={agentAppIconSrc.brandStudio}
          alt=""
          className="size-7 rounded-lg"
        />
        <div className="flex flex-col text-left">
          <span className="text-[10px] text-muted">Branding</span>
          <span className="text-xs font-semibold">Apparel brand context</span>
        </div>
      </div>
    </div>
  );
}

export function CustomDomainsGraphic() {
  return isOsmanthusEnabled() ? (
    <OsmanthusCustomDomainsGraphic />
  ) : (
    <LegacyCustomDomainsGraphic />
  );
}

function OsmanthusCustomDomainsGraphic() {
  return (
    <div className="relative h-[260px] w-full max-w-[460px]">
      <PaperCard className="absolute inset-x-6 bottom-1 top-8 overflow-hidden border-brand-seashell/15 bg-brand-seashell/10 p-2 shadow-lg">
        <img
          src={grazaOriginal}
          alt="A published storefront on its branded domain"
          className="size-full rounded-lg object-cover object-top"
        />
      </PaperCard>
      <PaperCard className="absolute left-1/2 top-2 z-10 flex h-12 w-[320px] -translate-x-1/2 items-center justify-center gap-2 rounded-full border-0.5 border-brand-seashell/20 bg-sheet/90 px-5 shadow-md supports-backdrop-filter:backdrop-blur-sm">
        <Globe className="size-4 text-muted" />
        <span className="font-sans text-sm font-medium tracking-tight text-default">
          shop.graza.co
        </span>
      </PaperCard>
    </div>
  );
}

export function SelectAndChatGraphic() {
  return isOsmanthusEnabled() ? (
    <OsmanthusSelectAndChatGraphic />
  ) : (
    <LegacySelectAndChatGraphic />
  );
}

function OsmanthusSelectAndChatGraphic() {
  return (
    <div className="grid w-full max-w-[460px] grid-cols-[1.18fr_0.82fr] gap-3">
      <PaperCard className="relative h-[260px] min-w-0 overflow-hidden border-brand-seashell/15 bg-brand-seashell/10 p-2 shadow-lg">
        <img
          src={cometeerOriginal}
          alt="A coffee storefront page being edited"
          className="size-full rounded-lg object-cover object-top"
        />
        <div className="absolute inset-x-2 bottom-2 h-16 rounded-md border-2 border-fuchsia bg-fuchsia/5 shadow-[0_0_0_2px_color-mix(in_srgb,var(--color-fuchsia)_12%,transparent)]">
          <span className="absolute -top-6 left-0 rounded-md bg-fuchsia px-2 py-1 text-[9px] font-medium text-white shadow-sm">
            Product benefits
          </span>
          <span className="absolute -right-1 -top-1 size-2 rounded-full border border-white bg-fuchsia" />
          <span className="absolute -bottom-1 -left-1 size-2 rounded-full border border-white bg-fuchsia" />
          <MousePointer2 className="absolute -bottom-3 right-7 size-5 fill-sheet text-default drop-shadow-sm" />
        </div>
      </PaperCard>

      <PaperCard className="flex h-[260px] flex-col justify-end gap-3 border-brand-seashell/15 bg-sheet/95 p-3 shadow-lg supports-backdrop-filter:backdrop-blur-sm">
        <div className="flex flex-col gap-2">
          <div className="self-end rounded-2xl rounded-br-md bg-default px-3 py-2 text-left text-[10px] leading-snug text-sheet">
            <span className="mb-1 inline-flex rounded-md bg-fuchsia px-1.5 py-0.5 text-[8px] font-medium text-white">
              Product benefits
            </span>
            <span className="block">
              Turn this into a comparison with coffee pods: taste, cost, and
              prep time
            </span>
          </div>
          <div className="rounded-2xl rounded-bl-md border-0.5 border-border bg-surface px-3 py-2 text-left text-[10px] leading-snug text-default shadow-sm">
            Added a side-by-side comparison of taste, cost per cup, and prep
            time.
          </div>
        </div>
        <div className="flex items-center gap-2 border-t-0.5 border-border pt-3">
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-xl border-0.5 border-border bg-surface px-3 py-2 text-left shadow-sm">
            <span className="truncate text-[9px] text-muted">
              Ask for anything
            </span>
            <span className="flex size-5 shrink-0 items-center justify-center rounded-md bg-default text-sheet">
              <ArrowUp className="size-3" />
            </span>
          </div>
        </div>
      </PaperCard>
    </div>
  );
}

export function InsightsGraphic() {
  return isOsmanthusEnabled() ? (
    <OsmanthusInsightsGraphic />
  ) : (
    <LegacyInsightsGraphic />
  );
}

function OsmanthusInsightsGraphic() {
  return (
    <PaperCard className="flex w-full max-w-[420px] flex-col gap-3 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src={agentAppIconSrc.insights}
            alt=""
            className="size-8 rounded-lg"
          />
          <span className="text-sm font-semibold">Insights</span>
        </div>
        <span className="rounded-full bg-surface px-2 py-1 text-[10px] text-muted">
          This week
        </span>
      </div>
      <div className="overflow-hidden rounded-lg border-0.5 border-border bg-surface">
        <div className="flex items-start justify-between gap-3 px-2.5 pt-2.5">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <BarChart3 className="size-3.5" />
            Conversion trend
          </div>
          <div className="flex flex-col items-end">
            <span className="text-sm font-semibold text-default">4.8%</span>
            <span className="text-[10px] text-muted">↑ 0.9%</span>
          </div>
        </div>
        <div className="px-2.5">
          <svg
            viewBox="0 0 360 72"
            preserveAspectRatio="none"
            className="mt-3 block h-16 w-full"
            aria-hidden
          >
            <defs>
              <linearGradient
                id="insights-conversion-trend-fill"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor="var(--color-fuchsia)"
                  stopOpacity="0.22"
                />
                <stop
                  offset="100%"
                  stopColor="var(--color-fuchsia)"
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            <path
              d="M0 58 C44 54 72 34 106 40 C138 48 156 20 190 26 C226 34 248 16 286 20 C322 24 340 12 360 10 L360 72 L0 72 Z"
              fill="url(#insights-conversion-trend-fill)"
            />
            <path
              d="M0 58 C44 54 72 34 106 40 C138 48 156 20 190 26 C226 34 248 16 286 20 C322 24 340 12 360 10"
              fill="none"
              stroke="var(--color-fuchsia)"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
            <path
              d="M0 62 H360"
              fill="none"
              stroke="var(--color-border)"
              strokeLinecap="round"
            />
            <path
              d="M0 36 H360"
              fill="none"
              stroke="var(--color-border)"
              strokeOpacity="0.65"
              strokeDasharray="4 6"
              strokeLinecap="round"
            />
            <circle cx="358" cy="10" r="2.5" fill="var(--color-fuchsia)" />
          </svg>
        </div>
      </div>
      <div className="rounded-lg border-0.5 border-border bg-surface p-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium">Next move</span>
            <span className="text-[11px] leading-snug text-muted">
              Match the hero copy to the paid social offer.
            </span>
          </div>
          <div className="shrink-0 rounded-md bg-sheet px-2 py-1 text-right">
            <span className="block text-xs font-semibold text-default">
              +6-9%
            </span>
            <span className="block text-[9px] text-muted">
              expected CVR lift
            </span>
          </div>
        </div>
      </div>
    </PaperCard>
  );
}

function LegacyContextGraphic() {
  return (
    <div className="grid w-full max-w-[460px] grid-cols-[0.95fr_1.05fr] grid-rows-[128px_112px] gap-3">
      <PaperCard className="flex min-h-0 flex-col gap-3 p-3">
        <div className="flex items-center gap-3">
          <div className="size-12 rounded-lg bg-[#F8D56B]" />
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Bestseller hoodie</span>
            <span className="text-xs text-[#6F5738]">$88 · 4 colors</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="h-2 w-full rounded-full bg-[#EEEAE2]" />
          <span className="h-2 w-11/12 rounded-full bg-[#EEEAE2]" />
          <span className="h-2 w-3/5 rounded-full bg-[#EEEAE2]" />
        </div>
      </PaperCard>

      <PaperCard className="row-span-2 flex min-h-0 flex-col gap-3 p-3">
        <div className="flex items-center gap-2">
          <img
            src={agentAppIconSrc.brandStudio}
            alt=""
            className="size-7 rounded-lg"
          />
          <span className="text-sm font-semibold">Branding</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <span className="h-7 rounded-md bg-[#ef196e]" />
          <span className="h-7 rounded-md bg-[#ffeb80]" />
          <span className="h-7 rounded-md bg-[#1F1B16]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex h-14 flex-col justify-center rounded-lg border border-[rgba(111,87,56,0.16)] bg-white px-2">
            <span className="font-editorial text-2xl leading-none">Aa</span>
            <span className="text-[9px] text-[#6F5738]">Editorial</span>
          </div>
          <div className="flex h-14 flex-col justify-center rounded-lg border border-[rgba(111,87,56,0.16)] bg-white px-2">
            <span className="text-xl font-semibold leading-none">Aa</span>
            <span className="text-[9px] text-[#6F5738]">Sans</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-2 rounded-lg border border-[rgba(111,87,56,0.16)] bg-[#FFFEFA] p-2">
          <span className="text-[10px] font-medium text-[#6F5738]">Voice</span>
          <div className="flex flex-col gap-1.5">
            <span className="h-1.5 w-full rounded-full bg-[#EEEAE2]" />
            <span className="h-1.5 w-11/12 rounded-full bg-[#EEEAE2]" />
            <span className="h-1.5 w-2/3 rounded-full bg-[#EEEAE2]" />
          </div>
        </div>
      </PaperCard>

      <PaperCard className="flex min-h-0 flex-col gap-2 p-3">
        <span className="text-sm font-semibold">Assets and files</span>
        <div className="grid flex-1 grid-cols-2 gap-1.5">
          <div className="flex items-center gap-1.5 rounded-md border border-[rgba(111,87,56,0.14)] bg-white px-2 py-1">
            <ImageIcon className="size-3 text-[#6F5738]" />
            <span className="text-[9px] text-[#6F5738]">Hero.jpg</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-[rgba(111,87,56,0.14)] bg-white px-2 py-1">
            <FileText className="size-3 text-[#6F5738]" />
            <span className="text-[9px] text-[#6F5738]">Brief.pdf</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-[rgba(111,87,56,0.14)] bg-white px-2 py-1">
            <Type className="size-3 text-[#1F1B16]" />
            <span className="text-[9px] text-[#6F5738]">Brand.otf</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-md border border-[rgba(111,87,56,0.14)] bg-white px-2 py-1">
            <FileSpreadsheet className="size-3 text-[#6F5738]" />
            <span className="text-[9px] text-[#6F5738]">Copy.csv</span>
          </div>
        </div>
      </PaperCard>
    </div>
  );
}

function LegacyCustomDomainsGraphic() {
  return (
    <div className="relative h-[260px] w-full max-w-[460px]">
      <PaperCard className="absolute left-1/2 top-12 flex h-[196px] w-[360px] -translate-x-1/2 flex-col overflow-hidden p-0">
        <div className="flex h-9 items-center gap-2 border-b-[0.5px] border-[rgba(111,87,56,0.14)] bg-[#FFFEFA] px-3">
          <span className="size-1.5 rounded-full bg-[#D8C9B0]" />
          <span className="size-1.5 rounded-full bg-[#C9B89D]" />
          <span className="size-1.5 rounded-full bg-[#EEEAE2]" />
          <span className="ml-2 h-4 flex-1 rounded-md bg-[#FCFBF7]" />
        </div>
        <div className="flex flex-1 flex-col bg-[#FCFBF7]">
          <div className="grid h-16 grid-cols-[1fr_96px] items-center gap-4 bg-[#FFFEFA] px-5">
            <div className="flex flex-col gap-1.5">
              <span className="h-2.5 w-32 rounded-full bg-[#C9B89D]" />
              <span className="h-1.5 w-44 rounded-full bg-[#EEEAE2]" />
            </div>
            <span className="h-7 rounded-md bg-[#C9B89D]" />
          </div>
          <div className="grid flex-1 grid-cols-[1fr_112px] gap-4 p-4">
            <div className="flex flex-col gap-2">
              <span className="h-2 w-32 rounded-full bg-[#C9B89D]" />
              <span className="h-1.5 w-full rounded-full bg-[#EEEAE2]" />
              <span className="h-1.5 w-5/6 rounded-full bg-[#EEEAE2]" />
              <div className="mt-auto grid grid-cols-2 gap-2">
                <span className="h-9 rounded-lg bg-[#FFFEFA]" />
                <span className="h-9 rounded-lg bg-[#FFFEFA]" />
              </div>
            </div>
            <div className="rounded-lg bg-[#FFFEFA] p-2">
              <span className="block h-14 rounded-md bg-[#EEEAE2]" />
              <span className="mt-2 block h-1.5 rounded-full bg-[#EEEAE2]" />
              <span className="mt-1.5 block h-1.5 w-2/3 rounded-full bg-[#EEEAE2]" />
            </div>
          </div>
        </div>
      </PaperCard>
      <PaperCard className="absolute left-1/2 top-5 z-10 flex h-12 w-[320px] -translate-x-1/2 items-center justify-center gap-2 rounded-full border-[0.5px] border-[rgba(111,87,56,0.22)] bg-[#FFFEFA] px-5 shadow-md shadow-black/10">
        <Globe className="size-4 text-[#6F5738]" />
        <span className="font-mono text-sm font-semibold text-default">
          shop.yoursite.com
        </span>
      </PaperCard>
    </div>
  );
}

function LegacySelectAndChatGraphic() {
  return (
    <div className="grid w-full max-w-[460px] grid-cols-[1.18fr_0.82fr] gap-3">
      <PaperCard className="relative flex h-[260px] min-w-0 flex-col overflow-hidden p-0">
        <div className="flex h-12 items-center border-b border-[rgba(111,87,56,0.14)] px-3 py-3">
          <span className="font-mono text-[9px] text-[#6F5738]">
            /summer-drop
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-3 bg-[#FCFBF7] p-3">
          <div className="rounded-lg border border-[rgba(111,87,56,0.14)] bg-white p-3">
            <span className="mb-2 block h-2 w-24 rounded-full bg-[#1F1B16]" />
            <span className="block h-1.5 w-full rounded-full bg-[#EEEAE2]" />
            <span className="mt-1.5 block h-1.5 w-3/4 rounded-full bg-[#EEEAE2]" />
          </div>
          <div className="relative rounded-lg border border-[#EF196E] bg-[#EF196E]/5 p-2 shadow-[0_0_0_3px_rgba(239,25,110,0.08)]">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold">
                  Customer reviews
                </span>
                <span className="text-[9px] text-[#C98A2A]">★★★★★</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-white p-2">
                  <span className="mb-1.5 block h-1.5 w-10 rounded-full bg-[#1F1B16]" />
                  <span className="block h-1.5 w-full rounded-full bg-[#EEEAE2]" />
                  <span className="mt-1 block h-1.5 w-2/3 rounded-full bg-[#EEEAE2]" />
                </div>
                <div className="rounded-md bg-white p-2">
                  <span className="mb-1.5 block h-1.5 w-12 rounded-full bg-[#1F1B16]" />
                  <span className="block h-1.5 w-full rounded-full bg-[#EEEAE2]" />
                  <span className="mt-1 block h-1.5 w-3/4 rounded-full bg-[#EEEAE2]" />
                </div>
              </div>
            </div>
            <MousePointer2 className="absolute -bottom-3 right-8 size-5 fill-white text-[#1F1B16]" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <span className="h-12 rounded-lg bg-white" />
            <span className="h-12 rounded-lg bg-white" />
            <span className="h-12 rounded-lg bg-white" />
          </div>
        </div>
      </PaperCard>

      <PaperCard className="flex h-[260px] flex-col justify-end gap-2 p-3">
        <div className="flex flex-col gap-2">
          <div className="relative max-w-[158px] self-end rounded-2xl rounded-br-[4px] bg-[#1F1B16] px-3 py-2 text-[10px] leading-snug text-white">
            Make this{" "}
            <span className="inline-flex rounded-md bg-[#EF196E] px-1.5 py-0.5 text-[8px] font-medium text-white">
              Review section
            </span>{" "}
            feel more polished
            <svg
              className="absolute -right-[7px] bottom-0"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
              aria-hidden
            >
              <path d="M0 0C0 6 2.5 10 6 13Q7 13.5 8 14H0V0Z" fill="#1F1B16" />
            </svg>
          </div>
          <div className="relative max-w-[146px] rounded-2xl rounded-bl-[4px] bg-[#F8F5EE] px-3 py-2 text-[10px] leading-snug text-[#6F5738]">
            Done. The selected section now has tighter review cards and clearer
            social proof.
            <svg
              className="absolute -left-[7px] bottom-0 -scale-x-100"
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
              aria-hidden
            >
              <path d="M0 0C0 6 2.5 10 6 13Q7 13.5 8 14H0V0Z" fill="#F8F5EE" />
            </svg>
          </div>
        </div>
      </PaperCard>
    </div>
  );
}

function LegacyInsightsGraphic() {
  return (
    <PaperCard className="flex w-full max-w-[420px] flex-col gap-3 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src={agentAppIconSrc.insights}
            alt=""
            className="size-8 rounded-lg"
          />
          <span className="text-sm font-semibold">Insights</span>
        </div>
        <span className="rounded-full bg-[#F8F5EE] px-2 py-1 text-[10px] text-[#6F5738]">
          This week
        </span>
      </div>
      <div className="overflow-hidden rounded-lg border border-[rgba(111,87,56,0.18)] bg-white">
        <div className="flex items-start justify-between gap-3 px-2.5 pt-2.5">
          <div className="flex items-center gap-1.5 text-xs font-medium">
            <BarChart3 className="size-3.5" />
            Conversion trend
          </div>
          <div className="flex flex-col items-end">
            <span className="text-sm font-semibold text-[#1F1B16]">4.8%</span>
            <span className="text-[10px] text-[#6F5738]">↑ 0.9%</span>
          </div>
        </div>
        <div className="px-2.5">
          <svg
            viewBox="0 0 360 72"
            preserveAspectRatio="none"
            className="mt-3 block h-16 w-full"
            aria-hidden
          >
            <defs>
              <linearGradient
                id="insights-conversion-trend-fill"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#ef196e" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#ef196e" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 58 C44 54 72 34 106 40 C138 48 156 20 190 26 C226 34 248 16 286 20 C322 24 340 12 360 10 L360 72 L0 72 Z"
              fill="url(#insights-conversion-trend-fill)"
            />
            <path
              d="M0 58 C44 54 72 34 106 40 C138 48 156 20 190 26 C226 34 248 16 286 20 C322 24 340 12 360 10"
              fill="none"
              stroke="#ef196e"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
            <path
              d="M0 62 H360"
              fill="none"
              stroke="rgba(111,87,56,0.16)"
              strokeLinecap="round"
            />
            <path
              d="M0 36 H360"
              fill="none"
              stroke="rgba(111,87,56,0.1)"
              strokeDasharray="4 6"
              strokeLinecap="round"
            />
            <circle cx="358" cy="10" r="2.5" fill="#ef196e" />
          </svg>
        </div>
      </div>
      <div className="rounded-lg border border-[rgba(111,87,56,0.18)] bg-white p-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium">Next move</span>
            <span className="text-[11px] leading-snug text-[#6F5738]">
              Match the hero copy to the paid social offer.
            </span>
          </div>
          <div className="shrink-0 rounded-md bg-[#FCFBF7] px-2 py-1 text-right">
            <span className="block text-xs font-semibold text-[#1F1B16]">
              +6-9%
            </span>
            <span className="block text-[9px] text-[#6F5738]">
              expected CVR lift
            </span>
          </div>
        </div>
      </div>
    </PaperCard>
  );
}
